import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ListTodo,
  Loader2,
  PanelRight,
  PanelRightClose,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import TaskOriginBadge from '../Tasks/TaskOriginBadge';
import {
  isTaskOverdue,
  taskStatusUpdatePatch,
  taskTypeLabel,
  type NetworkingTaskRow,
} from '../../lib/networkingTasks';
import { SIDEBAR_IDLE_CLOSE_MS, SIDEBAR_PEEK_CLOSE_MS } from './AppSidebar';

const TASKS_SIDEBAR_STORAGE_KEY = 'engine-tasks-sidebar-open';
const LIST_LIMIT = 40;
/** Hover dwell before peek-open (ms). */
const TASKS_SIDEBAR_PEEK_OPEN_MS = 3_000;

function formatDue(dueAt: string): string {
  const d = new Date(dueAt);
  if (Number.isNaN(d.getTime())) return dueAt;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function contactLabel(task: NetworkingTaskRow): string {
  const sub = task.subscriber;
  const name = sub?.name?.trim();
  const email = sub?.email?.trim() || task.contact_email;
  if (name) return name;
  return email || '—';
}

export function readTasksSidebarOpenPreference(): boolean {
  try {
    const stored = localStorage.getItem(TASKS_SIDEBAR_STORAGE_KEY);
    if (stored === 'false') return false;
    if (stored === 'true') return true;
  } catch {
    /* ignore */
  }
  return false;
}

export function persistTasksSidebarOpenPreference(isOpen: boolean): void {
  try {
    localStorage.setItem(TASKS_SIDEBAR_STORAGE_KEY, String(isOpen));
  } catch {
    /* ignore */
  }
}

interface TasksSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  onExpand?: () => void;
  onAutoClose?: () => void;
}

const TasksSidebar: React.FC<TasksSidebarProps> = ({
  isOpen,
  onToggle,
  onExpand,
  onAutoClose,
}) => {
  const desktopSidebarRef = useRef<HTMLElement>(null);
  const pointerInSidebarRef = useRef(false);
  const leftSidebarAtRef = useRef<number | null>(null);
  const peekOpenedRef = useRef(false);
  const interactedSincePeekRef = useRef(false);
  const peekCloseTimerRef = useRef<number | null>(null);
  const peekOpenTimerRef = useRef<number | null>(null);

  const [tasks, setTasks] = useState<NetworkingTaskRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('tasks')
        .select(
          '*, subscriber:subscribers(id, name, email), transcript:transcripts(id, title, meeting_date)',
        )
        .in('status', ['pending', 'in_progress'])
        .lte('due_at', now)
        .order('due_at', { ascending: false })
        .limit(LIST_LIMIT);
      if (error) throw error;
      setTasks((data ?? []) as NetworkingTaskRow[]);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (isOpen) void fetchTasks();
  }, [isOpen, fetchTasks]);

  const overdueCount = useMemo(
    () => tasks.filter((t) => isTaskOverdue(t.due_at, t.status)).length,
    [tasks],
  );

  useEffect(() => {
    if (!isOpen) {
      peekOpenedRef.current = false;
      interactedSincePeekRef.current = false;
      if (peekCloseTimerRef.current != null) {
        window.clearTimeout(peekCloseTimerRef.current);
        peekCloseTimerRef.current = null;
      }
      if (peekOpenTimerRef.current != null) {
        window.clearTimeout(peekOpenTimerRef.current);
        peekOpenTimerRef.current = null;
      }
    }
  }, [isOpen]);

  const clearPeekOpenTimer = () => {
    if (peekOpenTimerRef.current != null) {
      window.clearTimeout(peekOpenTimerRef.current);
      peekOpenTimerRef.current = null;
    }
  };

  const handleDesktopSidebarPointerEnter = () => {
    pointerInSidebarRef.current = true;
    leftSidebarAtRef.current = null;
    if (peekCloseTimerRef.current != null) {
      window.clearTimeout(peekCloseTimerRef.current);
      peekCloseTimerRef.current = null;
    }

    if (!isOpen && onExpand) {
      clearPeekOpenTimer();
      peekOpenTimerRef.current = window.setTimeout(() => {
        peekOpenTimerRef.current = null;
        if (!pointerInSidebarRef.current) return;
        peekOpenedRef.current = true;
        interactedSincePeekRef.current = false;
        onExpand();
      }, TASKS_SIDEBAR_PEEK_OPEN_MS);
    }
  };

  const handleDesktopSidebarPointerLeave = () => {
    pointerInSidebarRef.current = false;
    clearPeekOpenTimer();

    if (peekOpenedRef.current && !interactedSincePeekRef.current && onAutoClose) {
      if (peekCloseTimerRef.current != null) {
        window.clearTimeout(peekCloseTimerRef.current);
      }
      peekCloseTimerRef.current = window.setTimeout(() => {
        peekCloseTimerRef.current = null;
        peekOpenedRef.current = false;
        onAutoClose();
      }, SIDEBAR_PEEK_CLOSE_MS);
      return;
    }

    leftSidebarAtRef.current = Date.now();
  };

  useEffect(() => {
    if (!isOpen || !onAutoClose) return;

    const sidebarEl = desktopSidebarRef.current;
    if (!sidebarEl) return;

    const clearPeekCloseTimer = () => {
      if (peekCloseTimerRef.current != null) {
        window.clearTimeout(peekCloseTimerRef.current);
        peekCloseTimerRef.current = null;
      }
    };

    const clearPeekOpenTimerOnUnmount = () => {
      if (peekOpenTimerRef.current != null) {
        window.clearTimeout(peekOpenTimerRef.current);
        peekOpenTimerRef.current = null;
      }
    };

    const markInside = () => {
      handleDesktopSidebarPointerEnter();
    };

    const markOutside = () => {
      handleDesktopSidebarPointerLeave();
    };

    const onSidebarInteract = () => {
      interactedSincePeekRef.current = true;
      peekOpenedRef.current = false;
      clearPeekCloseTimer();
    };

    const onFocusIn = (event: FocusEvent) => {
      if (sidebarEl.contains(event.target as Node)) {
        markInside();
      }
    };

    const onFocusOut = (event: FocusEvent) => {
      if (!sidebarEl.contains(event.relatedTarget as Node)) {
        markOutside();
      }
    };

    sidebarEl.addEventListener('pointerenter', markInside);
    sidebarEl.addEventListener('pointerleave', markOutside);
    sidebarEl.addEventListener('pointerdown', onSidebarInteract);
    sidebarEl.addEventListener('focusin', onFocusIn);
    sidebarEl.addEventListener('focusout', onFocusOut);

    if (sidebarEl.matches(':hover')) {
      markInside();
    } else {
      markOutside();
    }

    const intervalId = window.setInterval(() => {
      if (pointerInSidebarRef.current) return;
      if (peekOpenedRef.current && !interactedSincePeekRef.current) return;
      if (
        leftSidebarAtRef.current != null &&
        Date.now() - leftSidebarAtRef.current >= SIDEBAR_IDLE_CLOSE_MS
      ) {
        onAutoClose();
      }
    }, 1000);

    return () => {
      sidebarEl.removeEventListener('pointerenter', markInside);
      sidebarEl.removeEventListener('pointerleave', markOutside);
      sidebarEl.removeEventListener('pointerdown', onSidebarInteract);
      sidebarEl.removeEventListener('focusin', onFocusIn);
      sidebarEl.removeEventListener('focusout', onFocusOut);
      clearPeekCloseTimer();
      clearPeekOpenTimerOnUnmount();
      window.clearInterval(intervalId);
    };
  }, [isOpen, onAutoClose]);

  const handleToggle = () => {
    clearPeekOpenTimer();
    peekOpenedRef.current = false;
    interactedSincePeekRef.current = true;
    onToggle();
  };

  const markDone = async (id: string) => {
    setUpdatingId(id);
    try {
      const { error } = await supabase
        .from('tasks')
        .update(taskStatusUpdatePatch('done'))
        .eq('id', id);
      if (error) throw error;
      await fetchTasks();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const sidebarContent = (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className="px-3 py-3 shrink-0 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <ListTodo className="w-4 h-4 text-amber-500 shrink-0" />
              <span className="truncate">Due now & past</span>
            </h2>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">
              Newest first · no future due dates
            </p>
          </div>
          <button
            type="button"
            onClick={() => void fetchTasks()}
            disabled={loading}
            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 shrink-0"
            title="Refresh tasks"
            aria-label="Refresh tasks"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {!loading && tasks.length > 0 ? (
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
            <span className="font-semibold text-gray-900 dark:text-white">{tasks.length}</span>
            {tasks.length >= LIST_LIMIT ? '+' : ''} open
            {overdueCount > 0 ? (
              <span className="text-amber-700 dark:text-amber-300 font-medium">
                {' '}
                · {overdueCount} overdue
              </span>
            ) : null}
          </p>
        ) : null}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading && tasks.length === 0 ? (
          <div className="flex justify-center py-10 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : tasks.length === 0 ? (
          <p className="px-3 py-8 text-sm text-center text-gray-500 dark:text-gray-400">
            <CheckCircle2 className="w-7 h-7 mx-auto mb-2 text-emerald-500 opacity-80" />
            Nothing due yet.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {tasks.map((task) => {
              const busy = updatingId === task.id;
              const overdue = isTaskOverdue(task.due_at, task.status);
              const subscriberHref = task.subscriber_id
                ? `/subscribers/${task.subscriber_id}?tab=tasks`
                : null;
              const transcriptHref = task.transcript_id ? `/transcripts/${task.transcript_id}` : null;

              return (
                <li
                  key={task.id}
                  className="px-3 py-2.5 hover:bg-gray-50/80 dark:hover:bg-gray-900/30"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">
                        {task.title || taskTypeLabel(task.task_type)}
                      </p>
                      {transcriptHref ? (
                        <Link
                          to={transcriptHref}
                          className="text-xs text-teal-600 dark:text-teal-400 hover:underline truncate block mt-0.5"
                        >
                          {task.transcript?.title?.trim() || 'Open transcript'}
                        </Link>
                      ) : subscriberHref ? (
                        <Link
                          to={subscriberHref}
                          className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline truncate block mt-0.5"
                        >
                          {contactLabel(task)}
                        </Link>
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {contactLabel(task)}
                        </p>
                      )}
                      <p
                        className={`text-xs mt-1 ${overdue ? 'text-amber-700 dark:text-amber-300 font-medium' : 'text-gray-500 dark:text-gray-400'}`}
                      >
                        Due {formatDue(task.due_at)}
                        {overdue ? ' · overdue' : ''}
                      </p>
                      <div className="mt-1">
                        <TaskOriginBadge task={task} compact />
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={busy}
                      title="Mark done"
                      onClick={() => void markDone(task.id)}
                      className="shrink-0 p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 disabled:opacity-50"
                    >
                      {busy ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="px-3 py-2.5 border-t border-gray-100 dark:border-gray-700 shrink-0">
        <Link
          to="/tasks/board"
          className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
        >
          Open task board
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );

  const desktopSidebarToggle = (
    <div
      className={`mt-auto shrink-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 z-10 ${
        isOpen ? 'px-3 py-2' : 'px-1 py-2'
      }`}
    >
      <button
        type="button"
        onClick={handleToggle}
        className={`hidden lg:flex w-full items-center rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
          isOpen ? 'justify-start px-2 py-2' : 'justify-center p-2'
        }`}
        aria-label={isOpen ? 'Collapse tasks panel' : 'Expand tasks panel'}
        title={isOpen ? 'Collapse tasks panel' : 'Expand tasks panel'}
      >
        {isOpen ? (
          <PanelRightClose className="w-5 h-5 shrink-0" />
        ) : (
          <PanelRight className="w-5 h-5 shrink-0" />
        )}
      </button>
    </div>
  );

  return (
    <div
      className={`hidden lg:flex shrink-0 flex-col bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 transition-[width] duration-500 ease-in-out ${
        isOpen ? 'w-72' : 'w-10'
      }`}
    >
      <aside
        ref={desktopSidebarRef}
        onPointerEnter={handleDesktopSidebarPointerEnter}
        onPointerLeave={handleDesktopSidebarPointerLeave}
        className="sticky top-16 flex flex-col h-[calc(100vh-4rem)] w-full overflow-hidden"
        aria-label="Tasks sidebar"
      >
        {isOpen ? (
          <div className="w-72 flex-1 min-h-0 flex flex-col overflow-hidden">{sidebarContent}</div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col items-center pt-3" aria-hidden="true">
            <span title="Tasks">
              <ListTodo className="w-5 h-5 text-amber-500 shrink-0" />
            </span>
          </div>
        )}
        {desktopSidebarToggle}
      </aside>
    </div>
  );
};

export default TasksSidebar;
